import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SavedConstructionReports.css";

const RAW_API_BASE =
  process.env.REACT_APP_API_BASE_URL ? `${process.env.REACT_APP_API_BASE_URL}/api` : "http://localhost:8080/api";

// Remove trailing slash
const API_BASE = RAW_API_BASE.replace(/\/+$/, "");

// If URL already ends with /api then don't add it again
const API_PREFIX = API_BASE.endsWith("/api") ? API_BASE : `${API_BASE}/api`;
const FRONTEND_BASE =
  process.env.REACT_APP_FRONTEND_URL || window.location.origin;

/* =====================================================
   AUTHORIZATION HEADER
   ===================================================== */
const authHeaders = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

/* =====================================================
   SAFE JSON RESPONSE READER
   ===================================================== */
const readJson = async (response) => {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || "Unable to load construction reports.");
  }

  return data;
};

/* =====================================================
   PERSON NAME HELPER
   Supports populated user object and manual string value
   ===================================================== */
const getPersonName = (person) => {
  if (!person) return "-";

  if (typeof person === "string") {
    return person.trim() || "-";
  }

  return (
    person.name ||
    person.fullName ||
    person.displayName ||
    person.employeeName ||
    person.email ||
    "-"
  );
};

/* =====================================================
   RECEIVED BY HELPER
   Supports different backend field names
   ===================================================== */
const getReceivedByName = (report) => {
  return getPersonName(
    report.receivedBy ||
      report.reviewedBy ||
      report.receivedByName ||
      report.manualReceivedBy,
  );
};

/* =====================================================
   DATE AND TIME FORMATTER
   ===================================================== */
const formatDateTime = (dateValue) => {
  if (!dateValue) return "-";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* =====================================================
   DATE FORMATTER
   ===================================================== */
const formatDate = (dateValue) => {
  if (!dateValue) return "-";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* =====================================================
   PROJECT NAME HELPER
   ===================================================== */
const getProjectName = (report) => {
  return (
    report.projectName ||
    report.project?.projectName ||
    report.project?.name ||
    "Untitled Project"
  );
};

export default function SavedConstructionReports() {
  const navigate = useNavigate();

  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [errorMessage, setErrorMessage] = useState("");

  const [filters, setFilters] = useState({
    project: "",
    fromDate: "",
    toDate: "",
    search: "",
  });

  /* =====================================================
     UPDATE SINGLE FILTER
     ===================================================== */
  const updateFilter = (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  /* =====================================================
     LOAD PROJECT DROPDOWN
     ===================================================== */
  const loadProjects = useCallback(async () => {
    try {
      setLoadingProjects(true);

      const response = await fetch(`${API_BASE}/projects`, {
        headers: authHeaders(),
      });

      const data = await readJson(response);

      const projectList = Array.isArray(data)
        ? data
        : Array.isArray(data.projects)
          ? data.projects
          : [];

      setProjects(projectList);
    } catch (error) {
      console.error("Unable to load projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  /* =====================================================
     LOAD SAVED CONSTRUCTION REPORTS
     ===================================================== */
  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const params = new URLSearchParams();

      if (filters.project) {
        params.set("project", filters.project);
      }

      if (filters.fromDate) {
        params.set("fromDate", filters.fromDate);
      }

      if (filters.toDate) {
        params.set("toDate", filters.toDate);
      }

      const queryString = params.toString();

      const url = `${API_BASE}/construction-reports${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await fetch(url, {
        headers: authHeaders(),
      });

      const data = await readJson(response);

      const reportList = Array.isArray(data)
        ? data
        : Array.isArray(data.reports)
          ? data.reports
          : [];

      setReports(reportList);
    } catch (error) {
      console.error("Unable to load reports:", error);

      setReports([]);
      setErrorMessage(error.message || "Unable to load construction reports.");
    } finally {
      setLoading(false);
    }
  }, [filters.project, filters.fromDate, filters.toDate]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  /* =====================================================
     FRONTEND SEARCH FILTER
     Searches project, location and person names
     ===================================================== */
  const filteredReports = useMemo(() => {
    const searchText = filters.search.trim().toLowerCase();

    if (!searchText) {
      return reports;
    }

    return reports.filter((report) => {
      const projectName = getProjectName(report);
      const location = report.siteLocation?.address || "";
      const preparedBy = getPersonName(report.preparedBy);
      const receivedBy = getReceivedByName(report);

      return [projectName, location, preparedBy, receivedBy].some((value) =>
        String(value).toLowerCase().includes(searchText),
      );
    });
  }, [reports, filters.search]);

  /* =====================================================
     CLEAR ALL FILTERS
     ===================================================== */
  const clearFilters = () => {
    setFilters({
      project: "",
      fromDate: "",
      toDate: "",
      search: "",
    });
  };

  /* =====================================================
     OPEN REPORT VIEW PAGE
     ===================================================== */
  const handleViewReport = (reportId) => {
    navigate(`/construction-report?reportId=${encodeURIComponent(reportId)}`);
  };

  return (
    <div className="saved-construction-page">
      {/* ================= PAGE HEADER ================= */}
      <header className="scr-page-header">
        <div className="scr-header-content">
          <p className="scr-eyebrow">Operations / Architect</p>

          <h1>Saved Construction Reports</h1>

          <p className="scr-header-description">
            View reports project-wise and date-wise.
          </p>
        </div>

        <button
          type="button"
          className="scr-primary-btn"
          onClick={() => navigate("/construction-report")}
        >
          <span className="scr-button-plus">+</span>
          Create New Report
        </button>
      </header>

      {/* ================= FILTER SECTION ================= */}
      <section className="scr-filter-card">
        <div className="scr-filter-grid">
          <label className="scr-field">
            <span>Project</span>

            <select
              value={filters.project}
              disabled={loadingProjects}
              onChange={(event) => updateFilter("project", event.target.value)}
            >
              <option value="">
                {loadingProjects ? "Loading projects..." : "All projects"}
              </option>

              {projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.projectName || project.name || "Unnamed Project"}
                </option>
              ))}
            </select>
          </label>

          <label className="scr-field">
            <span>From Date</span>

            <input
              type="date"
              value={filters.fromDate}
              max={filters.toDate || undefined}
              onChange={(event) => updateFilter("fromDate", event.target.value)}
            />
          </label>

          <label className="scr-field">
            <span>To Date</span>

            <input
              type="date"
              value={filters.toDate}
              min={filters.fromDate || undefined}
              onChange={(event) => updateFilter("toDate", event.target.value)}
            />
          </label>

          <label className="scr-field scr-search-field">
            <span>Search</span>

            <input
              type="search"
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
              placeholder="Search project, location or person"
            />
          </label>

          <button
            type="button"
            className="scr-clear-btn"
            onClick={clearFilters}
            disabled={
              !filters.project &&
              !filters.fromDate &&
              !filters.toDate &&
              !filters.search
            }
          >
            Clear Filters
          </button>
        </div>
      </section>

      {/* ================= SUMMARY ================= */}
      <div className="scr-summary-row">
        <div className="scr-report-count">
          <strong>{filteredReports.length}</strong>
          <span>
            {filteredReports.length === 1 ? "Saved Report" : "Saved Reports"}
          </span>
        </div>

        <button
          type="button"
          className="scr-refresh-btn"
          disabled={loading}
          onClick={loadReports}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* ================= ERROR MESSAGE ================= */}
      {errorMessage && !loading && (
        <div className="scr-error-state">
          <strong>Unable to load reports</strong>
          <p>{errorMessage}</p>

          <button type="button" onClick={loadReports}>
            Try Again
          </button>
        </div>
      )}

      {/* ================= REPORT LIST ================= */}
      {!errorMessage &&
        (loading ? (
          <div className="scr-empty-state">
            <div className="scr-loader" />
            <p>Loading saved reports...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="scr-empty-state">
            <h3>No reports found</h3>

            <p>Change the project or date filter and try again.</p>
          </div>
        ) : (
          <div className="scr-report-list">
            {filteredReports.map((report) => {
              const projectName = getProjectName(report);

              const location = report.siteLocation?.address || "-";

              const observationCount = report.observations?.length || 0;

              return (
                <article key={report._id} className="scr-report-card">
                  {/* Main report information */}
                  <div className="scr-report-main">
                    <div className="scr-report-icon">CR</div>

                    <div className="scr-report-content">
                      <div className="scr-report-heading">
                        <h3>{projectName}</h3>

                        <p>Visit: {formatDateTime(report.visitDateTime)}</p>
                      </div>

                      <div className="scr-report-details">
                        <div className="scr-detail-item scr-location-detail">
                          <span>Location</span>

                          <strong title={location}>{location}</strong>
                        </div>

                        <div className="scr-detail-item">
                          <span>Prepared By</span>

                          <strong>{getPersonName(report.preparedBy)}</strong>
                        </div>

                        <div className="scr-detail-item">
                          <span>Received By</span>

                          <strong>{getReceivedByName(report)}</strong>
                        </div>

                        <div className="scr-detail-item">
                          <span>Observations</span>

                          <strong>{observationCount}</strong>
                        </div>

                        <div className="scr-detail-item">
                          <span>Saved On</span>

                          <strong>
                            {formatDate(report.createdAt || report.updatedAt)}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Saved badge and button remain aligned */}
                  <div className="scr-report-actions">
                    <span className="scr-status-badge">Saved</span>

                    <button
                      type="button"
                      className="scr-view-btn"
                      onClick={() => handleViewReport(report._id)}
                    >
                      View Report
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ))}
    </div>
  );
}

